import { ethers } from 'ethers';
import { getState } from 'src/store';
import { getProvider, getSigner } from '../wallet';

// Contract Deployments
import RegistryDeployment from '../../contracts/deployments/Registry.json';
import RegistrarDeployment from '../../contracts/deployments/Registrar.json';
import ResolverDeployment from '../../contracts/deployments/Resolver.json';

// Contract ABIs
import RegistryABI from '../../contracts/abis/Registry.json';
import RegistrarABI from '../../contracts/abis/Registrar.json';
import ResolverABI from '../../contracts/abis/Resolver.json';
import TokenABI from '../../contracts/abis/Token.json';
import { handleError } from '../errors/common';

type CNSContracts = 'Registry' | 'Registrar' | 'Resolver' | 'Token';

// The deployment entry (in src/contracts/deployments/*.json) the app runs against
export const ACTIVE_DEPLOYMENT = 'baseSepolia' as const;

export const callContractTransaction = async (
  contractType: CNSContracts,
  functionName: string,
  args: any[],
  contractAddress = '',
  payment: string | null = null,
) => {
  const { address, abi } = _getAddressAndAbi(contractType, contractAddress);

  if (abi && address.length > 0) {
    getState().setLoadingText(`Estimate transaction cost...`);
    const txCost = await _estimateTxCost(
      address,
      abi,
      functionName,
      args,
      payment,
    );
    getState().setLoadingText(``);
    const signer = await getSigner(txCost, functionName);
    const contract = new ethers.Contract(address, abi, signer);

    const txOptions: ethers.Overrides = {};

    if (payment) {
      txOptions.value = ethers.parseEther(payment);
    }

    getState().setLoadingText(
      `Calling contract ${contractType} with function ${functionName}...`,
    );
    await contract[functionName](...args, txOptions);
  }

  getState().setSnackbar({
    open: true,
    message: `Function ${functionName} successfully executed on ${contractType}.`,
    severity: 'success',
  });
  getState().setLoadingText('');
};

export const callContractView = async (
  contractType: CNSContracts,
  functionName: string,
  args: any[],
  contractAddress = '',
): Promise<any> => {
  try {
    const { address, abi } = _getAddressAndAbi(contractType, contractAddress);

    if (abi && address.length > 0) {
      getState().setLoadingText(
        `Calling contract ${contractType} with function ${functionName}...`,
      );
      const contract = new ethers.Contract(address, abi, getProvider());
      const result = await contract[functionName](...args);
      getState().setLoadingText('');
      return result;
    }
  } catch (error) {
    handleError(error);
  }
  getState().setLoadingText('');
};

// Helper functions
const _getAddressAndAbi = (
  contractType: CNSContracts,
  contractAddress = '',
): { address: string; abi: ethers.InterfaceAbi | null } => {
  let address = '';
  let abi = null;
  if (contractType === 'Registry') {
    address = RegistryDeployment[ACTIVE_DEPLOYMENT].address;
    abi = RegistryABI;
  } else if (contractType === 'Registrar') {
    address = RegistrarDeployment[ACTIVE_DEPLOYMENT].address;
    abi = RegistrarABI;
  } else if (contractType === 'Resolver') {
    address =
      contractAddress.length > 0
        ? contractAddress
        : ResolverDeployment[ACTIVE_DEPLOYMENT].address;
    abi = ResolverABI;
  } else if (contractType === 'Token') {
    address = contractAddress;
    abi = TokenABI;
  }
  return {
    address,
    abi,
  };
};

const _estimateTxCost = async (
  contractAddress: string,
  contractAbi: ethers.InterfaceAbi,
  functionName: string,
  args: any[],
  payment: string | null = null,
): Promise<any> => {
  const wallet = getState().wallet;

  if (wallet) {
    const provider = getProvider();
    const contract = new ethers.Contract(
      contractAddress,
      contractAbi,
      provider,
    );

    const tx: ethers.TransactionLike = {
      to: contractAddress,
      from: wallet.address,
      data: contract.interface.encodeFunctionData(functionName, args),
    };

    if (payment) {
      tx.value = ethers.parseEther(payment);
    }

    const gasEstimate = await provider.estimateGas(tx);
    const gasPrice = (await provider.getFeeData()).gasPrice;

    if (gasPrice) {
      const transactionCostWei = BigInt(gasEstimate) * BigInt(gasPrice);
      const transactionCostEth = ethers.formatUnits(
        transactionCostWei,
        'ether',
      );
      return transactionCostEth;
    }
  }
};
